// Registro de contatos enviados pelo admin (WhatsApp) para não reenviar
// mensagens repetidas para o mesmo usuário.
import { supabase } from "@/integrations/supabase/client";

export interface AdminContactRecord {
  id: string;
  target_user_id: string;
  contact_type: string;
  channel: string;
  sent_by: string | null;
  notes: string | null;
  created_at: string;
}

export const CONTACT_TYPE_LABELS: Record<string, string> = {
  geral: "Acompanhamento geral",
  feedback: "Pedir feedback",
  ajuda: "Oferecer ajuda",
  incentivo: "Incentivar uso das ferramentas",
  beneficios_guru: "Benefícios + Guru Chat",
  ativacao: "Confirmação de ativação",
  expired: "Plano vencido",
  unconfirmed: "E-mail não confirmado",
  cold: "Lead frio",
};

export const contactTypeLabel = (type: string) =>
  CONTACT_TYPE_LABELS[type] || type;

/** Grava o contato. Nunca lança erro — falha silenciosa não bloqueia o WhatsApp. */
export const recordAdminContact = async (params: {
  targetUserId: string;
  contactType: string;
  channel?: string;
  notes?: string | null;
}): Promise<AdminContactRecord | null> => {
  try {
    const { data: auth } = await supabase.auth.getUser();
    const { data, error } = await supabase
      .from("admin_user_contacts" as any)
      .insert({
        target_user_id: params.targetUserId,
        contact_type: params.contactType,
        channel: params.channel || "whatsapp",
        sent_by: auth?.user?.id ?? null,
        notes: params.notes ?? null,
      } as any)
      .select()
      .maybeSingle();

    if (error) {
      console.error("Erro ao registrar contato do admin:", error);
      return null;
    }
    return (data as any) || null;
  } catch (err) {
    console.error("Erro ao registrar contato do admin:", err);
    return null;
  }
};

/** Último contato registrado para um usuário. */
export const fetchLastAdminContact = async (
  targetUserId: string
): Promise<AdminContactRecord | null> => {
  try {
    const { data, error } = await supabase
      .from("admin_user_contacts" as any)
      .select("*")
      .eq("target_user_id", targetUserId)
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) return null;
    return (data as any) || null;
  } catch {
    return null;
  }
};

/** Histórico completo (mais recente primeiro). */
export const fetchAdminContacts = async (
  targetUserId: string,
  limit = 20
): Promise<AdminContactRecord[]> => {
  try {
    const { data, error } = await supabase
      .from("admin_user_contacts" as any)
      .select("*")
      .eq("target_user_id", targetUserId)
      .order("created_at", { ascending: false })
      .limit(limit);

    if (error || !data) return [];
    return data as any as AdminContactRecord[];
  } catch {
    return [];
  }
};

export const daysSinceDate = (dateString?: string | null): number | null => {
  if (!dateString) return null;
  const ms = Date.now() - new Date(dateString).getTime();
  return Math.max(0, Math.floor(ms / (1000 * 60 * 60 * 24)));
};

export const formatContactDateShort = (dateString?: string | null): string => {
  if (!dateString) return "—";
  try {
    return new Intl.DateTimeFormat("pt-BR", {
      day: "2-digit",
      month: "2-digit",
    }).format(new Date(dateString));
  } catch {
    return "—";
  }
};
